import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getProgramBySlug } from '../../data/programs';
import { getProgress } from '../../lib/storage/progressStore';
import { saveTest, getTests } from '../../lib/storage/testStore';
import { track } from '../../lib/analytics/track';
import Navbar from '../../components/Navbar';
import { ArrowLeft, CheckCircle2, AlertCircle, ArrowRight, Lock } from 'lucide-react';

export default function UnitTest() {
  const { programSlug, unitSlug } = useParams();
  const navigate = useNavigate();
  const program = getProgramBySlug(programSlug);
  const unit = program?.units.find((u) => u.slug === unitSlug);

  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [existingTest, setExistingTest] = useState(null);

  useEffect(() => {
    if (program && unit) {
      track('test_open', { programSlug: program.slug, unitSlug });

      // Verificar si ya completó el audio
      const progress = getProgress(program.slug);
      if (!progress[unitSlug]?.completedAt) {
        // No ha completado el audio, bloquear acceso
        return;
      }

      // Verificar si ya hizo el test
      const tests = getTests(program.slug);
      if (tests[unitSlug]) {
        setExistingTest(tests[unitSlug]);
      }
    }
  }, [program, unit, programSlug, unitSlug]);

  if (!program || !unit) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Reflexión no encontrada
            </h1>
            <Link
              to="/programas"
              className="text-blue-600 hover:text-blue-700 font-semibold"
            >
              ← Volver a programas
            </Link>
          </div>
        </div>
      </>
    );
  }

  // Verificar si completó el audio
  const progress = getProgress(program.slug);
  if (!progress[unitSlug]?.completedAt) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md mx-auto px-6 text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Audio no completado
            </h1>
            <p className="text-gray-600 mb-6">
              Debes escuchar la conversación completa antes de acceder a las preguntas de reflexión.
            </p>
            <Link
              to={`/p/${program.slug}/u/${unit.slug}`}
              className="inline-flex items-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver a la conversación
            </Link>
          </div>
        </div>
      </>
    );
  }

  const handleAnswerChange = (questionIndex, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionIndex]: parseInt(value),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validar que todas las preguntas están respondidas
    const allAnswered = unit.test.questions.every(
      (_, i) => answers[i] !== undefined
    );

    if (!allAnswered) {
      alert('Por favor responde todas las preguntas');
      return;
    }

    // Calcular score total
    const score = Object.values(answers).reduce((sum, val) => sum + val, 0);

    // Determinar perfil
    const profile = unit.test.profiles.find(
      (p) => score >= p.range[0] && score <= p.range[1]
    );

    if (!profile) {
      console.error('No se encontró un perfil para el score:', score);
      alert('Error: No se pudo determinar tu perfil. Contacta al soporte.');
      return;
    }

    const testResult = {
      answers: Object.values(answers),
      score,
      profileKey: profile.key,
      profileLabel: profile.label,
      profileAdvice: profile.advice,
    };

    // Guardar en localStorage
    saveTest(program.slug, unit.slug, testResult);

    // Track
    track('test_submit', {
      programSlug: program.slug,
      unitSlug,
      score,
      profile: profile.key,
    });

    setResult(testResult);
  };

  // Encontrar siguiente unidad
  const currentIndex = program.units.findIndex((u) => u.slug === unit.slug);
  const nextUnit = program.units[currentIndex + 1];
  const isNextUnitLocked = nextUnit?.locked === true;

  // Si ya tiene un resultado (nuevo o previo)
  const displayResult = result || existingTest;

  if (displayResult) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen" style={{ backgroundColor: '#F7F9FB' }}>
          <section className="max-w-3xl mx-auto px-6 py-8 sm:py-12">
            <Link
              to={`/p/${program.slug}/u/${unit.slug}`}
              className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-8 transition-colors text-sm"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Volver a la conversación
            </Link>

            {/* Síntesis de reflexión */}
            <div className="bg-white rounded-lg shadow-sm p-8 sm:p-10 mb-8">
              <div className="mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
                  Tu estilo actual de influencia
                </h1>
                <p className="text-xl text-slate-700 mb-4">
                  Predomina: {displayResult.profileLabel}
                </p>
                <p className="text-sm text-slate-600">
                  Nivel de alineación: {displayResult.score}/15
                </p>
              </div>

              <div className="border-l-4 border-emerald-600 bg-emerald-50 rounded-r-lg p-6 mb-6">
                <h3 className="text-lg font-bold text-slate-900 mb-3">
                  Recomendación principal
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  {displayResult.profileAdvice}
                </p>
              </div>

              {/* Frase de cierre reflexiva */}
              <p className="text-sm text-slate-600 italic text-center border-t border-slate-200 pt-6">
                Esta reflexión no te califica. Te muestra desde dónde estás influyendo hoy.
              </p>
            </div>

            {/* Integración académica (solo al completar U3) */}
            {!nextUnit && unit.slug === 'u3-challenger' && program.sources && (
              <div className="bg-white border border-slate-200 rounded-lg p-6 mb-8">
                <p className="text-sm text-slate-600 leading-relaxed">
                  Esta reflexión integra principios de psicología social, 
                  investigación empírica en ventas y modelos de influencia estratégica 
                  desarrollados por Cialdini, Rackham y Dixon & Adamson.
                </p>
              </div>
            )}

            {/* Navegación editorial */}
            <div className="flex justify-between items-center pt-4 border-t border-slate-200">
              <Link
                to={`/p/${program.slug}`}
                className="text-slate-600 hover:text-slate-900 text-sm font-medium flex items-center gap-2 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Volver al programa
              </Link>

              {nextUnit && (
                isNextUnitLocked ? (
                  <div className="text-slate-400 text-sm font-medium flex items-center gap-2 cursor-not-allowed">
                    Siguiente conversación
                    <Lock className="w-3.5 h-3.5" />
                  </div>
                ) : (
                  <Link
                    to={`/p/${program.slug}/u/${nextUnit.slug}`}
                    className="text-slate-600 hover:text-slate-900 text-sm font-medium flex items-center gap-2 transition-colors"
                  >
                    Siguiente conversación
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                )
              )}

              {!nextUnit && (
                <Link
                  to={`/p/${program.slug}`}
                  className="text-emerald-600 hover:text-emerald-700 text-sm font-semibold flex items-center gap-2 transition-colors"
                >
                  Programa completado
                  <CheckCircle2 className="w-4 h-4" />
                </Link>
              )}
            </div>
          </section>
        </main>
      </>
    );
  }

  // Formulario de reflexión
  return (
    <>
      <Navbar />
      <main className="min-h-screen" style={{ backgroundColor: '#F7F9FB' }}>
        <section className="bg-white border-b border-slate-200">
          <div className="max-w-3xl mx-auto px-6 py-6 sm:py-8">
            <Link
              to={`/p/${program.slug}/u/${unit.slug}`}
              className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6 transition-colors text-sm"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Volver a la conversación
            </Link>

            <div className="space-y-2">
              <p className="text-sm text-slate-600">
                Para reflexionar sobre:
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">
                {unit.title}
              </h1>
            </div>
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-6 py-8 sm:py-12">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Contexto intelectual */}
            <div className="prose prose-slate max-w-none">
              <p className="text-base text-slate-700 leading-relaxed italic border-l-4 border-emerald-600 pl-6 py-2">
                Estas preguntas no miden tu nivel. Buscan ayudarte a observar cómo estás influyendo hoy. Respóndelas con honestidad intelectual, no para "aprobar".
              </p>
            </div>

            {/* Preguntas como tarjetas separadas */}
            <div className="space-y-6">
                {unit.test.questions.map((question, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm p-6 sm:p-8">
                    <h3 className="text-lg font-semibold text-slate-900 mb-6">
                      {index + 1}. {question}
                    </h3>

                    <div className="space-y-3">
                      {[1, 2, 3, 4, 5].map((value) => {
                        const isSelected = answers[index] === value;
                        return (
                          <label
                            key={value}
                            className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                              isSelected
                                ? 'border-emerald-600 bg-emerald-50 shadow-md'
                                : 'border-gray-200 hover:border-emerald-400 hover:bg-emerald-50/30'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`question-${index}`}
                              value={value}
                              checked={isSelected}
                              onChange={(e) => handleAnswerChange(index, e.target.value)}
                              className="w-5 h-5 text-emerald-600 focus:ring-emerald-500"
                              required
                            />
                            <span className={`flex-1 font-medium ${
                              isSelected ? 'text-emerald-900' : 'text-gray-700'
                            }`}>
                              {value === 1 && 'Nada cierto para mí'}
                              {value === 2 && 'Poco cierto'}
                              {value === 3 && 'Neutral'}
                              {value === 4 && 'Bastante cierto'}
                              {value === 5 && 'Muy cierto para mí'}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

            {/* CTA editorial */}
            <div className="flex justify-center pt-4">
              <button
                type="submit"
                className="inline-flex items-center gap-2 bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
              >
                Ver mis reflexiones
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </form>
        </section>
      </main>
    </>
  );
}
